import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Lock, Unlock, Clock, Plus, CreditCard, Gift, Trash2, RefreshCw, ArrowRightLeft } from 'lucide-react';
import CashCalculator from '../components/CashCalculator';

const catStyles = {
  fundador: 'bg-purple-100 text-purple-800',
  normal: 'bg-stone-100 text-stone-800',
  frecuente: 'bg-orange-100 text-orange-800',
  eventos: 'bg-blue-100 text-blue-800',
};
const paymentMethods = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'cortesia', label: 'Cortesia' },
  { value: 'cupon', label: 'Cupon' },
];
const expReasons = [
  'Mala experiencia del cliente', 'Cliente VIP / Fundador', 'Promocion especial',
  'Compensacion por servicio', 'Evento especial', 'Cortesia del gerente', 'Error en el cobro'
];

function getDuration(t) {
  if (!t) return '0m';
  const d = Date.now() - new Date(t).getTime();
  const h = Math.floor(d / 3600000), m = Math.floor((d % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function LockerMapPage() {
  const { API, user: currentUser } = useAuth();
  const [lockers, setLockers] = useState([]);
  const [perRow, setPerRow] = useState(10);
  const [total, setTotal] = useState(50);
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [visitDetail, setVisitDetail] = useState(null);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [activeVisitsNoLocker, setActiveVisitsNoLocker] = useState([]);

  // Dialogs
  const [showAddOrder, setShowAddOrder] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showExperience, setShowExperience] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [orderForm, setOrderForm] = useState({ product_id: '', quantity: 1 });
  const [paymentMethod, setPaymentMethod] = useState('efectivo');
  const [expType, setExpType] = useState('');
  const [expReason, setExpReason] = useState('');
  const [expOrderId, setExpOrderId] = useState('');
  const [assignVisitId, setAssignVisitId] = useState('');
  const [newLockerNumber, setNewLockerNumber] = useState('');

  const canCheckout = currentUser?.role === 'admin' || currentUser?.role === 'cajero';

  const fetchLockers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lockers/status`);
      setLockers(data.lockers);
      setPerRow(data.per_row);
      setTotal(data.total);
    } catch {}
  }, [API]);

  const fetchHelpers = useCallback(async () => {
    try {
      const [p, s, v] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/visits/active`)
      ]);
      setProducts(p.data.filter(pr => pr.active));
      setSettings(s.data);
      setActiveVisitsNoLocker(v.data.filter(vi => !vi.locker_number));
    } catch {}
  }, [API]);

  useEffect(() => { fetchLockers(); fetchHelpers(); }, [fetchLockers, fetchHelpers]);
  useEffect(() => { const i = setInterval(fetchLockers, 15000); return () => clearInterval(i); }, [fetchLockers]);

  const handleLockerClick = async (locker) => {
    setSelectedLocker(locker);
    if (locker.status === 'occupied' && locker.visit_id) {
      try {
        const { data } = await axios.get(`${API}/visits/${locker.visit_id}`);
        setVisitDetail(data);
      } catch { setVisitDetail(null); }
    } else {
      setVisitDetail(null);
    }
  };

  const closeSheet = () => { setSelectedLocker(null); setVisitDetail(null); };

  const handleAddOrder = async () => {
    if (!orderForm.product_id || !visitDetail) return;
    try {
      const { data } = await axios.post(`${API}/visits/${visitDetail.id}/orders`, orderForm);
      setVisitDetail(data);
      setShowAddOrder(false);
      setOrderForm({ product_id: '', quantity: 1 });
      toast.success('Producto agregado');
      fetchLockers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleCheckout = async () => {
    if (!visitDetail) return;
    try {
      await axios.post(`${API}/visits/${visitDetail.id}/checkout`, { payment_method: paymentMethod, notes: '' });
      toast.success('Cobro realizado');
      setShowCheckout(false);
      closeSheet();
      fetchLockers();
      fetchHelpers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleExperience = async () => {
    if (!expType || !expReason || !visitDetail) return;
    try {
      const body = { type: expType, reason: expReason };
      if (expType === 'cancel_product') body.order_id = expOrderId;
      const { data } = await axios.post(`${API}/visits/${visitDetail.id}/experience`, body);
      setVisitDetail(data.visit);
      setShowExperience(false);
      setExpType(''); setExpReason(''); setExpOrderId('');
      toast.success('Cortesia aplicada');
      fetchLockers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleRelease = async (number) => {
    if (!window.confirm(`Liberar locker #${number}?`)) return;
    try {
      await axios.post(`${API}/lockers/${number}/release`);
      toast.success(`Locker #${number} liberado`);
      closeSheet();
      fetchLockers();
      fetchHelpers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleReassign = async () => {
    const num = parseInt(newLockerNumber);
    if (!num || !selectedLocker) { toast.error('Selecciona un numero de locker'); return; }
    try {
      await axios.post(`${API}/lockers/${selectedLocker.number}/reassign`, { new_number: num });
      toast.success(`Reasignado de #${selectedLocker.number} a #${num}`);
      setShowReassign(false);
      setNewLockerNumber('');
      closeSheet();
      fetchLockers();
      fetchHelpers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleAssign = async (number) => {
    if (!assignVisitId) { toast.error('Selecciona una visita'); return; }
    try {
      await axios.post(`${API}/lockers/${number}/assign`, { visit_id: assignVisitId });
      toast.success(`Locker #${number} asignado`);
      setShowAssign(false);
      setAssignVisitId('');
      closeSheet();
      fetchLockers();
      fetchHelpers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const occupiedCount = lockers.filter(l => l.status === 'occupied').length;
  const freeCount = lockers.filter(l => l.status === 'free').length;

  const discountPct = visitDetail && settings ? (settings.category_discounts?.[visitDetail.client_category] || 0) : 0;
  const subtotal = visitDetail?.subtotal || 0;
  const expDisc = visitDetail?.experience_discount || 0;
  const catDisc = Math.round(subtotal * discountPct / 100 * 100) / 100;
  const finalTotal = Math.max(0, Math.round((subtotal - catDisc - expDisc) * 100) / 100);

  const rows = [];
  for (let i = 0; i < lockers.length; i += perRow) {
    rows.push(lockers.slice(i, i + perRow));
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locker-map-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Mapa de Lockers</h1>
          <p className="text-stone-500 text-sm mt-1">{occupiedCount} ocupados / {freeCount} libres de {total}</p>
        </div>
        <Button data-testid="refresh-lockers" onClick={() => { fetchLockers(); fetchHelpers(); }} variant="outline" className="rounded-full border-stone-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </div>

      <div className="flex gap-4 text-xs">
        <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-emerald-500"></div><span className="text-stone-600">Libre</span></div>
        <div className="flex items-center gap-1.5"><div className="w-4 h-4 rounded bg-orange-500"></div><span className="text-stone-600">Ocupado</span></div>
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <CardContent className="p-6">
          <div className="space-y-2">
            {rows.map((row, ri) => (
              <div key={ri} className="flex gap-2 flex-wrap">
                {row.map(locker => (
                  <button
                    key={locker.number}
                    data-testid={`locker-${locker.number}`}
                    onClick={() => handleLockerClick(locker)}
                    className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex flex-col items-center justify-center text-xs font-medium transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer border-2 ${
                      locker.status === 'occupied'
                        ? 'bg-orange-50 border-orange-400 text-orange-800 hover:bg-orange-100 shadow-sm'
                        : 'bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100'
                    }`}
                  >
                    <span className="font-bold text-sm">{locker.number}</span>
                    {locker.status === 'occupied' ? (
                      <Lock className="w-3 h-3 mt-0.5" strokeWidth={2} />
                    ) : (
                      <Unlock className="w-3 h-3 mt-0.5 opacity-40" strokeWidth={1.5} />
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Locker Detail Sheet */}
      <Sheet open={!!selectedLocker} onOpenChange={(o) => { if (!o) closeSheet(); }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {selectedLocker && (
            <div className="space-y-5">
              <SheetHeader>
                <SheetTitle className="font-outfit text-xl flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${selectedLocker.status === 'occupied' ? 'bg-orange-500' : 'bg-emerald-500'}`}></span>
                  Locker #{selectedLocker.number}
                </SheetTitle>
              </SheetHeader>

              {selectedLocker.status === 'free' && (
                <div className="text-center py-8 space-y-4">
                  <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto">
                    <Unlock className="w-8 h-8 text-emerald-600" strokeWidth={1.5} />
                  </div>
                  <p className="text-stone-600 font-medium">Locker disponible</p>
                  {canCheckout && activeVisitsNoLocker.length > 0 && (
                    <Button data-testid="assign-locker-btn" onClick={() => setShowAssign(true)} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">
                      Asignar a visita activa
                    </Button>
                  )}
                  {activeVisitsNoLocker.length === 0 && (
                    <p className="text-xs text-stone-400">No hay visitas sin locker asignado</p>
                  )}
                </div>
              )}

              {selectedLocker.status === 'occupied' && visitDetail && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-900">{visitDetail.client_name}</p>
                      <Badge className={`${catStyles[visitDetail.client_category] || catStyles.normal} border-0 rounded-full text-xs mt-1`}>{visitDetail.client_category}</Badge>
                    </div>
                    <div className="flex items-center gap-1 text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
                      <Clock className="w-3 h-3" />
                      <span className="text-xs font-medium">{getDuration(visitDetail.entry_time)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-stone-50 rounded-xl p-3">
                      <p className="text-xs text-stone-500">Mesero</p>
                      <p className="text-sm font-medium text-stone-900 mt-0.5">{visitDetail.waiter_name}</p>
                    </div>
                    <div className="bg-stone-50 rounded-xl p-3">
                      <p className="text-xs text-stone-500">Entrada</p>
                      <p className="text-sm font-medium text-stone-900 mt-0.5">{new Date(visitDetail.entry_time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    {visitDetail.bracelet_code && (
                      <div className="bg-orange-50 rounded-xl p-3 col-span-2">
                        <p className="text-xs text-stone-500">Pulsera RFID</p>
                        <p className="text-sm font-mono font-bold text-orange-700 mt-0.5">{visitDetail.bracelet_code}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-outfit font-medium text-stone-900">Consumos</h3>
                      <Button data-testid="locker-add-order" onClick={() => setShowAddOrder(true)} size="sm" className="bg-orange-700 hover:bg-orange-800 text-white rounded-full text-xs h-8 px-4">
                        <Plus className="w-3 h-3 mr-1" /> Agregar
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {(visitDetail.orders || []).map(o => (
                        <div key={o.id} className="flex items-center justify-between bg-stone-50 rounded-xl p-3">
                          <div>
                            <p className="text-sm font-medium text-stone-900">{o.product_name}</p>
                            <p className="text-xs text-stone-500">{o.quantity} x ${o.unit_price.toFixed(2)}</p>
                          </div>
                          <span className="text-sm font-semibold text-stone-900">${o.total.toFixed(2)}</span>
                        </div>
                      ))}
                      {(visitDetail.orders || []).length === 0 && <p className="text-sm text-stone-400 text-center py-4">Sin consumos</p>}
                    </div>
                  </div>

                  <div className="border-t border-stone-200 pt-4">
                    {expDisc > 0 && (
                      <div className="flex justify-between mb-1 text-sm text-purple-700"><span>Cortesia</span><span>-${expDisc.toFixed(2)}</span></div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-outfit text-lg font-semibold text-stone-900">Total</span>
                      <span className="font-outfit text-2xl font-bold text-orange-700">${finalTotal.toFixed(2)}</span>
                    </div>
                    {canCheckout && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Button data-testid="locker-experience" onClick={() => setShowExperience(true)} className="flex-1 bg-purple-700 hover:bg-purple-800 text-white rounded-full text-sm h-10">
                            <Gift className="w-4 h-4 mr-1" /> Experiencia
                          </Button>
                          <Button data-testid="locker-checkout" onClick={() => setShowCheckout(true)} className="flex-1 bg-green-700 hover:bg-green-800 text-white rounded-full text-sm h-10">
                            <CreditCard className="w-4 h-4 mr-1" /> Cobrar
                          </Button>
                        </div>
                        <Button data-testid="locker-release" onClick={() => handleRelease(selectedLocker.number)} variant="outline" className="w-full rounded-full text-sm h-9 border-red-200 text-red-700 hover:bg-red-50">
                          <Unlock className="w-3.5 h-3.5 mr-1" /> Liberar Locker
                        </Button>
                        <Button data-testid="locker-reassign" onClick={() => { setNewLockerNumber(''); setShowReassign(true); }} variant="outline" className="w-full rounded-full text-sm h-9 border-blue-200 text-blue-700 hover:bg-blue-50">
                          <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Reasignar Locker
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Assign Locker Dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Asignar Locker #{selectedLocker?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Label>Seleccionar visita activa (sin locker)</Label>
            <Select value={assignVisitId} onValueChange={setAssignVisitId}>
              <SelectTrigger data-testid="assign-visit-select" className="rounded-xl"><SelectValue placeholder="Seleccionar visita" /></SelectTrigger>
              <SelectContent>
                {activeVisitsNoLocker.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.client_name} - {getDuration(v.entry_time)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="assign-confirm" onClick={() => handleAssign(selectedLocker?.number)} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Asignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Order Dialog */}
      <Dialog open={showAddOrder} onOpenChange={setShowAddOrder}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Agregar Producto</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Producto</Label>
              <Select value={orderForm.product_id} onValueChange={v => setOrderForm({ ...orderForm, product_id: v })}>
                <SelectTrigger data-testid="locker-order-product" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - ${p.price.toFixed(2)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cantidad</Label><Input data-testid="locker-order-qty" type="number" min="1" value={orderForm.quantity} onChange={e => setOrderForm({ ...orderForm, quantity: parseInt(e.target.value) || 1 })} className="rounded-xl mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOrder(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="locker-order-submit" onClick={handleAddOrder} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Agregar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Cobrar</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-center py-2">
              <p className="text-stone-500 text-sm">Total a cobrar</p>
              <p className="font-outfit text-4xl font-bold text-orange-700">${finalTotal.toFixed(2)}</p>
            </div>
            <div>
              <Label>Metodo de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="locker-payment" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{paymentMethods.map(pm => <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {paymentMethod === 'efectivo' && <CashCalculator total={finalTotal} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="locker-checkout-confirm" onClick={handleCheckout} className="bg-green-700 hover:bg-green-800 text-white rounded-full">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Experience Dialog */}
      <Dialog open={showExperience} onOpenChange={setShowExperience}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="font-outfit text-purple-900">Experiencia / Cortesia</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Tipo</Label>
              <Select value={expType} onValueChange={v => { setExpType(v); setExpOrderId(''); }}>
                <SelectTrigger data-testid="locker-exp-type" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_courtesy">Cortesia completa ($0)</SelectItem>
                  <SelectItem value="cancel_entry_fee">Cancelar cuota de entrada</SelectItem>
                  <SelectItem value="cancel_product">Cancelar producto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {expType === 'cancel_product' && visitDetail && (
              <div>
                <Label>Producto</Label>
                <Select value={expOrderId} onValueChange={setExpOrderId}>
                  <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {(visitDetail.orders || []).filter(o => o.product_id !== 'entry_fee').map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.product_name} x{o.quantity} - ${o.total.toFixed(2)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Razon</Label>
              <Select value={expReason} onValueChange={setExpReason}>
                <SelectTrigger data-testid="locker-exp-reason" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent>{expReasons.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExperience(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="locker-exp-submit" onClick={handleExperience} className="bg-purple-700 hover:bg-purple-800 text-white rounded-full">Aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Locker Dialog */}
      <Dialog open={showReassign} onOpenChange={setShowReassign}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">Reasignar Locker #{selectedLocker?.number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-stone-600">Cliente: <strong>{visitDetail?.client_name}</strong></p>
            <div>
              <Label>Nuevo numero de locker</Label>
              <Select value={newLockerNumber} onValueChange={setNewLockerNumber}>
                <SelectTrigger data-testid="reassign-locker-select" className="rounded-xl mt-1"><SelectValue placeholder="Seleccionar locker disponible" /></SelectTrigger>
                <SelectContent>
                  {lockers.filter(l => l.status === 'free').map(l => (
                    <SelectItem key={l.number} value={String(l.number)}>Locker #{l.number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReassign(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="reassign-confirm" onClick={handleReassign} className="bg-blue-700 hover:bg-blue-800 text-white rounded-full">Reasignar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
